import {Button} from '@mui/material';
import CustomTable, {ICustomTableColumn} from '~/components/CustomTable';
import FrontdeskSearchbox from '~/components/FrontdeskSearchBox';
import FrontdeskButton from '~/components/FrontdeskButton';
import SearchRefractionIcon from '~/assets/svgs/search-refraction.svg?react';
import LeftArrowIcon from '~/assets/svgs/chevron-left-arrow.svg?react';
import RigthArrowIcon from '~/assets/svgs/chevron-right-arrow.svg?react';
import NewPaymentIcon from '$assets/svgs/new-payment.svg?react';
import {
  PAGE_SIZE,
  COLUMN_WIDTHS,
  INSURANCE_CLAIMS_STRINGS,
  DEFAULT_VALUES,
  InsuranceClaimsOrderBy,
} from './constants';
import {InsuranceClaim} from '$api/finance/insuranceClaims/insuranceList';
import SearchPatientContainer from '../../common/components/SearchPatientModel';
import {useState} from 'react';
import './styles.scss';
import {useNavigate} from 'react-router-dom';
import {RoutePath} from '~/constants/routes';
import {RouteSection, getRoute} from '~/utils/route';
import StatusChip from './StatusChip';

interface InsuranceClaimsComponentProps {
  isLoading: boolean;
  isError: boolean;
  message: string;
  searchText: string;
  currentPage: number;
  claims: InsuranceClaim[];
  totalPages: number;
  totalRecords: number;
  sortBy?: InsuranceClaimsOrderBy | string | undefined;
  sortDirection?: 'asc' | 'desc';
  status?: string;
  onSearchTextChange: (value: string) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  onStatusChange: (status: string | undefined) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  allClaimsCount: number;
  newClaimCount: number;
  pendingCount: number;
  submittedCount: number;
  rejectedCount: number;
  paidCount: number;
  outstandingCount: number;
}

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  isLoading?: boolean;
}

interface StatusFilterButtonProps {
  label: string;
  count: number;
  isActive: boolean;
  onStatusFilterClick: () => void;
  isLoading?: boolean;
}

const StatusFilterButton = ({
  label,
  count,
  isActive,
  onStatusFilterClick,
  isLoading,
}: StatusFilterButtonProps) => {
  const getTestId = (label: string) => {
    const normalized = label.toLowerCase().replace(/ /g, '-');
    return `status-filter-${normalized}`;
  };

  return (
    <Button
      variant="outlined"
      className={`h-8 px-2 py-1 text-sm font-normal insurance-status-button ${
        isActive ? 'active' : ''
      }`}
      onClick={onStatusFilterClick}
      disabled={isLoading}
      data-testid={getTestId(label)}
    >
      {label} ({count})
    </Button>
  );
};

const PaginationControls = ({
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  isLoading,
}: PaginationControlsProps) => (
  <div className="pagination-container flex">
    <Button
      className="p-1 px-2 flex items-center page-change-button"
      onClick={onPreviousPage}
      disabled={isLoading || currentPage === 1}
      data-testid="previous-page-button"
    >
      <LeftArrowIcon />
    </Button>
    <div className="p-1 px-6 flex items-center border-left border-solid border-x border-y-0 border-color">
      <span>
        {currentPage.toString().padStart(2, '0')} /{' '}
        {Math.max(totalPages || 1, 1)
          .toString()
          .padStart(2, '0')}
      </span>
    </div>
    <Button
      className="p-1 px-2 flex items-center page-change-button"
      onClick={onNextPage}
      disabled={isLoading || currentPage === totalPages}
      data-testid="next-page-button"
    >
      <RigthArrowIcon />
    </Button>
  </div>
);

const AmountCell = ({amount}: {amount: number}) => {
  if (
    amount === null ||
    amount === undefined ||
    isNaN(amount) ||
    amount === 0
  ) {
    return <span>-</span>;
  }
  return (
    <span>
      {DEFAULT_VALUES.CURRENCY_SYMBOL}
      {amount.toFixed(2)}
    </span>
  );
};

const getStatusFilters = (
  allClaimsCount: number,
  newClaimCount: number,
  pendingCount: number,
  submittedCount: number,
  rejectedCount: number,
  paidCount: number,
  outstandingCount: number
) => [
  {
    label: INSURANCE_CLAIMS_STRINGS.ALL_CLAIMS,
    count: allClaimsCount,
    status: undefined,
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.NEW_CLAIM,
    count: newClaimCount,
    status: 'new_claim',
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.PENDING,
    count: pendingCount,
    status: 'pending',
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.SUBMITTED,
    count: submittedCount,
    status: 'submitted',
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.REJECTED,
    count: rejectedCount,
    status: 'rejected',
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.PAID,
    count: paidCount,
    status: 'paid',
  },
  {
    label: INSURANCE_CLAIMS_STRINGS.OUTSTANDING,
    count: outstandingCount,
    status: 'outstanding',
  },
];

const getSortKeyForColumn = (columnKey: string): string => {
  const keyMapping: Record<string, string> = {
    membershipNo: InsuranceClaimsOrderBy.MembershipNo,
    patientName: InsuranceClaimsOrderBy.PatientName,
    outstandingAmount: InsuranceClaimsOrderBy.OutstandingAmount,
    insurerName: InsuranceClaimsOrderBy.InsurerName,
    status: InsuranceClaimsOrderBy.Status,
  };

  return keyMapping[columnKey] || columnKey;
};

const getColumnKeyForSortKey = (sortKey: string): string => {
  const reverseMapping: Record<string, string> = {
    [InsuranceClaimsOrderBy.MembershipNo]: 'membershipNo',
    [InsuranceClaimsOrderBy.PatientName]: 'patientName',
    [InsuranceClaimsOrderBy.OutstandingAmount]: 'outstandingAmount',
    [InsuranceClaimsOrderBy.InsurerName]: 'insurerName',
    [InsuranceClaimsOrderBy.Status]: 'status',
  };

  return reverseMapping[sortKey] || sortKey;
};

const InsuranceClaimsComponent = (props: InsuranceClaimsComponentProps) => {
  const {
    isLoading,
    isError,
    message,
    searchText,
    currentPage,
    claims,
    totalPages,
    totalRecords,
    sortBy,
    sortDirection,
    status,
    onSearchTextChange,
    setCurrentPage,
    onSortChange,
    onStatusChange,
    onNextPage,
    onPreviousPage,
    allClaimsCount,
    newClaimCount,
    pendingCount,
    submittedCount,
    rejectedCount,
    paidCount,
    outstandingCount,
  } = props;

  const [newClaimModalOpen, setNewClaimModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleOnChange = (searchQuery = '') => {
    setCurrentPage(1);
    onSearchTextChange(searchQuery);
  };

  const currentRangeStart =
    (currentPage - 1) * PAGE_SIZE + (claims.length ? 1 : 0);
  const currentRangeEnd =
    currentRangeStart + claims.length - (claims.length ? 1 : 0);
  const summary = `Displaying ${currentRangeStart} - ${currentRangeEnd} of ${totalRecords}`;

  const renderWithDash = (value: unknown): React.ReactNode =>
    value === null || value === undefined || value === '' ? (
      <span>-</span>
    ) : (
      <span>{String(value)}</span>
    );

  const columns: ICustomTableColumn<Record<string, unknown>>[] = [
    {
      key: 'membershipNo',
      label: 'Membership Number',
      className: COLUMN_WIDTHS.membershipNumber,
      isSortable: true,
      render: renderWithDash,
    },
    {
      key: 'status',
      label: 'Status',
      className: COLUMN_WIDTHS.status,
      render: value => <StatusChip status={String(value)} />,
    },
    {
      key: 'patientName',
      label: 'Patient Name',
      className: COLUMN_WIDTHS.patientName,
      isSortable: true,
      render: renderWithDash,
    },
    {
      key: 'insurerName',
      label: 'Insurance Company',
      className: COLUMN_WIDTHS.insurerName,
      render: renderWithDash,
    },
    {
      key: 'outstandingAmount',
      label: 'Outstanding Amount',
      className: COLUMN_WIDTHS.amount,
      isSortable: true,
      render: value => <AmountCell amount={Number(value)} />,
    },
  ];

  const handleRowClick = (row: Record<string, unknown>) => {
    if (row && row.id) {
      navigate(
        getRoute(
          RouteSection.Finance,
          RoutePath.staff.finance.insuranceClaimDetails.replace(
            ':id',
            String(row.id)
          )
        )
      );
    }
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    const sortKey = getSortKeyForColumn(key);
    onSortChange(sortKey, direction);
  };

  const statusFilters = getStatusFilters(
    allClaimsCount,
    newClaimCount,
    pendingCount,
    submittedCount,
    rejectedCount,
    paidCount,
    outstandingCount
  );

  const tableSortBy = sortBy ? getColumnKeyForSortKey(sortBy) : undefined;

  return (
    <div className="flex flex-col flex-1 font-primary bg-primary overflow-auto">
      <main className="flex flex-col justify-start flex-1 mt-4 ml-10 mr-10 mb-6 overflow-x-hidden overflow-y-auto">
        <div className="flex items-center justify-between mx-2 my-4">
          <h1 className="flex1 font-semibold text-2xl leading-7 tracking-tight text-primary-dark">
            Insurance claims
          </h1>
          <div className="flex gap-4 items-center">
            <FrontdeskButton
              title="Submit a claim"
              startIcon={<NewPaymentIcon />}
              onClick={() => setNewClaimModalOpen(true)}
            />
            <FrontdeskSearchbox
              value={searchText}
              startIcon={<SearchRefractionIcon />}
              onChange={handleOnChange}
              placeholder="Search claim"
            />
          </div>
        </div>

        {isError && (
          <div
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded"
            data-testid="error-message"
          >
            <span className="text-red-600">{message}</span>
          </div>
        )}

        <div className="flex gap-4 mb-6 items-center justify-between">
          <div className="flex gap-4">
            {statusFilters.map(({label, count, status: filterStatus}) => (
              <StatusFilterButton
                key={filterStatus || 'all'}
                label={label}
                count={count}
                isActive={status === filterStatus}
                onStatusFilterClick={() => onStatusChange(filterStatus)}
                isLoading={isLoading}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-auto">
          <div className="insurance-claims-table-wrapper mb-4">
            <CustomTable
              columns={columns}
              data={claims as unknown as Record<string, unknown>[]}
              loading={isLoading}
              totalCount={totalRecords}
              showPagination={false}
              className="insurance-claims-table"
              onSortChange={handleSortChange}
              sortBy={tableSortBy}
              sortDirection={sortDirection}
              onRowClick={handleRowClick}
            />
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            isLoading={isLoading}
          />
          <div className="pr-10">
            <span className="text-sm tertiary">{summary}</span>
          </div>
        </div>
      </main>

      {newClaimModalOpen && (
        <SearchPatientContainer
          newAppointmentModalOpen={newClaimModalOpen}
          handleNewAppointmentModalOpen={() => setNewClaimModalOpen(false)}
          type="insurance_claim"
        />
      )}
    </div>
  );
};

export default InsuranceClaimsComponent;
